
/**
 * Module dependencies.
 */

import { HistoryItemProps, HistoryProps } from 'src/types/histories';
import { color, media, units } from 'src/styles/utils';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp } from 'styled-tools';
import Container from 'src/components/core/layout/container';
import Disclosure from 'src/components/disclosure';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import HistoryItem from 'src/components/histories/history-item';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, { ReactElement, useState } from 'react';
import Type from 'src/components/core/typography';
import styled, { css } from 'styled-components';
import useHistories from 'src/api/app/histories/use-histories';
import usePage from 'src/api/app/pages/use-page';

/**
 * Export `PageProps` type.
 */

export type PageProps = {
  description: string,
  descriptionSeo?: string | null,
  id: string,
  modules: any[],
  slug: string,
  subtitle?: string | null,
  text?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  histories: HistoryProps[],
  isKiosk?: boolean,
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  padding: 114px 0 150px;
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title title    . .'
    '. .     collapse . .';
  grid-template-columns: 1fr 1.5fr 9fr 1.5fr 1fr;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  margin-bottom: ${units(4.5)};
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `CollapseWrapper` styled component.
 */

const CollapseWrapper = styled.div`
  border-top: 1px solid ${color('grey100')};
  grid-area: collapse;
`;

/**
 * `DisclosureContent` styled component.
 */

const DisclosureContent = styled.div`
  margin-bottom: 78px;
`;

/**
 * `Collapse` component.
 */

const Collapse = ({ children, name }): ReactElement => {
  const [isOpen, setOpen] = useState<boolean>();

  return (
    <Disclosure
      header={(
        <Type.H4 as={'h2'}>
          {name}
        </Type.H4>
      )}
      isOpen={isOpen}
      scrollOnOpen
      toggle={() => setOpen(!isOpen)}
    >
      <DisclosureContent>
        {children}
      </DisclosureContent>
    </Disclosure>
  );
};

/**
 * `HistoryPage` component.
 */

const HistoryPage = (props: Props): ReactElement => {
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: histories,
    isLoading: isLoadingHistories,
    isSuccess: isSuccessHistories
  } = useHistories({ initialData: props.histories });

  const subtitle = pageData?.subtitle;
  const title = pageData?.title;
  const isLoading = isLoadingPage || isLoadingHistories;
  const isSuccess = isSuccessPage && isSuccessHistories;

  return (
    <>
      <Metatags
        description={pageData?.descriptionSeo || pageData?.description}
        hrefLangs={getHrefLangs(pageData?.translations, 'history')}
        isKiosk={props?.isKiosk}
        pageTitle={title}
        title={pageData?.titleSeo}
      />

      <Section isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <Container hasPadding={!props?.isKiosk}>
            <Grid>
              <Title>
                <ParallaxFadeInUp>
                  {`${title} `}

                  {subtitle && (
                    <SubTitle>
                      {subtitle}
                    </SubTitle>
                  )}
                </ParallaxFadeInUp>
              </Title>

              <CollapseWrapper>
                {histories.map(({ id, items, name }: HistoryProps) => (
                  <FadeInUpAnimation
                    key={id}
                    options={{ threshold: 0.15 }}
                  >
                    <Collapse name={name}>
                      {items.map((history: HistoryItemProps, index: number) => (
                        <HistoryItem
                          key={index}
                          {...history}
                        />
                      ))}
                    </Collapse>
                  </FadeInUpAnimation>
                ))}
              </CollapseWrapper>
            </Grid>
          </Container>
        )}
      </Section>
    </>
  );
};

/**
 * Export `HistoryPage` component.
 */

export default HistoryPage;
